import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../core/service/api-service/api-service';
import { PermissionService } from '../../../core/service/permission/permission.service';

import { SwalUtils } from '../../../shared/utils/swal.utils';
import { isAllowedImage, readImagePreview, IMAGE_FORMAT_ERROR } from '../../../shared/utils/image-upload';
interface Partner {
  id: number;
  name: string;
  link_url: string | null;
  category: string;
  status: 'ACTI' | 'INAC';
  file_id: number | null;
  file_link: string | null;
  created_date: string;
}

@Component({
  selector: 'app-partenaires',
  standalone: false,
  templateUrl: './partenaires.component.html',
  styleUrls: ['./partenaires.component.scss'],
})
export class PartenairesComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  partenaires  = signal<Partner[]>([]);
  isLoading    = signal(true);
  errorMessage = signal<string | null>(null);

  filterType   = signal<'' | 'PARTP' | 'PARTO'>('');
  filterStatus = signal<'' | 'ACTI' | 'INAC'>('');

  pendingStatus = signal<{ id: number; status: 'ACTI' | 'INAC' } | null>(null);

  addModal        = signal(false);
  editingPartner  = signal<Partner | null>(null);
  addType         = signal<'PARTP' | 'PARTO'>('PARTP');
  addName         = signal('');
  addLinkUrl      = signal('');
  addUploading    = signal(false);
  addFile         = signal<File | null>(null);
  addPreviewUrl   = signal<string | null>(null);
  addError        = signal<string | null>(null);

  get isEditMode(): boolean { return this.editingPartner() !== null; }

  private destroy$ = new Subject<void>();

  constructor(private api: ApiService) {}

  ngOnInit(): void { this.load(); }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.api.post('/api/partenaires/list', {
      status: this.filterStatus() || null,
      type:   this.filterType()   || null,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.partenaires.set(res.data?.all || []);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Erreur lors du chargement des partenaires');
        this.isLoading.set(false);
      },
    });
  }

  onFilterChange(): void { this.load(); }

  categoryLabel(type: string): string {
    return type === 'PARTP' ? 'Principal' : type === 'PARTO' ? 'Officiel' : type;
  }

  statusLabel(status: string): string {
    return status === 'ACTI' ? 'En ligne' : 'Hors ligne';
  }

  statusBadgeClass(status: string): string {
    return status === 'ACTI'
      ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
      : 'bg-amber-50 text-amber-600 border-amber-100';
  }

  openAddModal(): void {
    this.editingPartner.set(null);
    this.addType.set('PARTP');
    this.addName.set('');
    this.addLinkUrl.set('');
    this.addFile.set(null);
    this.addPreviewUrl.set(null);
    this.addError.set(null);
    this.addModal.set(true);
  }

  openEditModal(p: Partner): void {
    this.editingPartner.set(p);
    this.addType.set(p.category as 'PARTP' | 'PARTO');
    this.addName.set(p.name);
    this.addLinkUrl.set(p.link_url || '');
    this.addFile.set(null);
    this.addPreviewUrl.set(p.file_link);
    this.addError.set(null);
    this.addModal.set(true);
  }

  closeAddModal(): void {
    if (!this.isEditMode) {
      const url = this.addPreviewUrl();
      if (url) URL.revokeObjectURL(url);
    }
    this.addPreviewUrl.set(null);
    this.addFile.set(null);
    this.editingPartner.set(null);
    this.addModal.set(false);
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!isAllowedImage(file)) { SwalUtils.error('Format invalide', IMAGE_FORMAT_ERROR); return; }
    this.addFile.set(file);
    readImagePreview(file, (url) => this.addPreviewUrl.set(url));
  }

  submitAdd(): void {
    if (!this.addName().trim()) { this.addError.set('Le nom est requis'); return; }
    this.addError.set(null);
    const form = new FormData();
    form.append('name', this.addName().trim());
    form.append('type', this.addType());
    if (this.addLinkUrl().trim()) form.append('link_url', this.addLinkUrl().trim());
    const file = this.addFile();
    if (file) form.append('image', file);
    this.addUploading.set(true);

    const editing = this.editingPartner();
    const req$ = editing
      ? this.api.put(`/api/partenaires/${editing.id}`, form)
      : this.api.post('/api/partenaires', form);

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        if (!this.isEditMode) {
          const url = this.addPreviewUrl();
          if (url) URL.revokeObjectURL(url);
        }
        this.addUploading.set(false);
        this.addFile.set(null);
        this.addPreviewUrl.set(null);
        this.editingPartner.set(null);
        this.addModal.set(false);
        this.load();
      },
      error: () => { this.addUploading.set(false); this.addError.set('Erreur lors de l\'enregistrement'); },
    });
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  confirmStatus(id: number, status: 'ACTI' | 'INAC'): void {
    this.pendingStatus.set({ id, status });
  }

  cancelStatus(): void { this.pendingStatus.set(null); }

  applyStatus(): void {
    const pending = this.pendingStatus();
    if (!pending) return;
    this.pendingStatus.set(null);
    this.api.patch(`/api/partenaires/${pending.id}/status`, { status: pending.status })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.load(),
        error: () => this.errorMessage.set('Erreur lors du changement de statut'),
      });
  }
}
